import { SortValue, SortDirection, TableConfig, RichTableState } from './types';

export const sortValueRows = (value1: SortValue, value2: SortValue, direction: SortDirection) => {
    const getReturnValue = (ascValue: number) => (direction === 'asc' ? ascValue : -ascValue);
    const maybeConvertDateToNumber = (value: SortValue) => {
        if (value instanceof Date) {
            return value.getTime();
        }
        return value;
    };

    const maybeConvertedValue1 = maybeConvertDateToNumber(value1);
    const maybeConvertedValue2 = maybeConvertDateToNumber(value2);

    if (maybeConvertedValue1 === maybeConvertedValue2) {
        return 0;
    } else if (maybeConvertedValue1 === null) {
        return getReturnValue(1);
    } else if (maybeConvertedValue2 === null) {
        return getReturnValue(-1);
    } else if (typeof maybeConvertedValue1 === 'string' && typeof maybeConvertedValue2 === 'string') {
        return getReturnValue(maybeConvertedValue1.localeCompare(maybeConvertedValue2));
    } else if (typeof maybeConvertedValue1 === 'number' && typeof maybeConvertedValue2 === 'number') {
        return getReturnValue(maybeConvertedValue1 - maybeConvertedValue2);
    } else if (typeof maybeConvertedValue1 === 'number') {
        return getReturnValue(-1);
    } else {
        return getReturnValue(1);
    }
};

const getSortPropKey = (colIndex: number) => `sortProp${colIndex}`;

type SearchAugmentedDataElement<TDataElement> = TDataElement & {
    searchProps: any;
};

export const augmentDataWithSearchProps = <TDataElement>(
    config: TableConfig<TDataElement>,
    data: TDataElement[],
): SearchAugmentedDataElement<TDataElement>[] => {
    return data.map((dataElement) => {
        const searchProps: any = {};
        config.columns.forEach((column, index) => {
            const searchValue = column.getSearchValue?.(dataElement);
            if (searchValue !== undefined) {
                searchProps[getSortPropKey(index)] = searchValue;
            }
        });
        return {
            ...dataElement,
            searchProps,
        };
    });
};

export const stripSearchProps = <TDataElement>(data: SearchAugmentedDataElement<TDataElement>[]): TDataElement[] => {
    return data.map((dataElement) => {
        const { searchProps, ...strippedDataElement } = dataElement;
        return strippedDataElement as TDataElement;
    });
};

export const filterAndSortData = <TDataElement>(
    config: TableConfig<TDataElement>,
    data: TDataElement[],
    state: RichTableState,
) => {
    const filteredByDiscreetValues = data.filter((dataElement) => {
        return config.columns.every((column) => {
            const filterState = state.columnFilters[column.columnKey];
            if (!filterState || !filterState.selectedDiscreetValues || !filterState.selectedDiscreetValues.length) {
                return true;
            }
            const discreetValue = column.getDiscreetValue?.(dataElement);
            if (!discreetValue) {
                return false;
            }

            const asArray = Array.isArray(discreetValue) ? discreetValue : [discreetValue];
            return asArray.some((value) => filterState.selectedDiscreetValues!.includes(value));
        });
    });

    const augmentedData = augmentDataWithSearchProps(config, filteredByDiscreetValues);
    const searchKeys = config.columns.map((column, index) => getSortPropKey(index));
    const searchFilteredData = augmentedData.filter((dataElement) => {
        if (!state.searchValue) {
            return true;
        }

        return searchKeys.some((searchKey) => {
            const searchValue = dataElement.searchProps[searchKey];
            if (searchValue === undefined) {
                return true;
            }

            const columnValue = dataElement.searchProps[searchKey];
            if (columnValue === null || columnValue === undefined) {
                return false;
            }

            return String(searchValue).toLowerCase().includes(state.searchValue.toLowerCase());
        });
    });
    const strippedData = stripSearchProps(searchFilteredData);

    if (!state.sortState) {
        return strippedData;
    }

    const sortColumnIndex = config.columns.findIndex((column) => column.columnKey === state.sortState!.columnKey);
    if (sortColumnIndex === -1) {
        return strippedData;
    }

    const sortValueGetter = config.columns[sortColumnIndex].getSortValue;
    if (!sortValueGetter) {
        return strippedData;
    }

    return strippedData.sort((dataElement1, dataElement2) => {
        const value1 = sortValueGetter(dataElement1);
        const value2 = sortValueGetter(dataElement2);
        return sortValueRows(value1, value2, state.sortState!.direction);
    });
};

export const TABLE_PADDING = 12;
